import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormats, useTemplateInFormat, useTemplatesForFormat } from '@/hooks/useTemplates';
import type { TemplateSummary } from '@/types/api';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';
import { TemplatePreview } from '@/components/app/TemplatePreview';
import {
  DESIGN_CANVAS_HEIGHT_PX,
  DESIGN_CANVAS_WIDTH_PX,
} from '@/creativeComposition/elementMerge';
import { CUSTOM_FORMAT_PATH_ID } from '@/services/formats';

const FORMAT_ICONS: Record<string, string> = {
  standard: 'rectangle',
  animation: 'movie',
  carousel: 'view_carousel',
  multi: 'groups',
  interaction: 'touch_app',
  ar: 'view_in_ar',
  custom: 'palette',
};

export default function FormatsPage() {
  const [selectedFormatPathId, setSelectedFormatPathId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const navigate = useNavigate();

  const { data: formats, loading: formatsLoading, error: formatsError } = useFormats();
  const formatList = useMemo(() => formats ?? [], [formats]);
  const activeFormatPathId = selectedFormatPathId ?? formatList[0]?.id ?? null;

  const { data: templates, loading: templatesLoading, error: templatesError } =
    useTemplatesForFormat(activeFormatPathId);
  const templateDetail = useTemplateInFormat(activeFormatPathId, selectedTemplateId);

  const allTemplates = useMemo(() => templates ?? [], [templates]);

  const selectedFormatMeta = useMemo(
    () => formatList.find((f) => f.id === activeFormatPathId) ?? null,
    [formatList, activeFormatPathId],
  );

  const selectedTemplate = useMemo(
    () => allTemplates.find((t) => t.id === selectedTemplateId) ?? null,
    [allTemplates, selectedTemplateId],
  );

  const previewWidth = Number(
    templateDetail.data?.render_settings?.width_px ?? DESIGN_CANVAS_WIDTH_PX,
  );
  const previewHeight = Number(
    templateDetail.data?.render_settings?.height_px ?? DESIGN_CANVAS_HEIGHT_PX,
  );
  const previewAspect =
    Number.isFinite(previewWidth) && Number.isFinite(previewHeight) && previewHeight > 0
      ? `${previewWidth} / ${previewHeight}`
      : `${DESIGN_CANVAS_WIDTH_PX} / ${DESIGN_CANVAS_HEIGHT_PX}`;

  function handleUseTemplate() {
    if (!selectedTemplate) return;
    setIsNewCreativeOpen(true);
  }

  const error = formatsError || templatesError;

  return (
    <div className="p-4 md:p-8 flex flex-col gap-8 min-h-[calc(100vh-4rem)]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
        <div className="col-span-1 lg:col-span-5 flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">
                Select Format
              </p>
            </div>
            {formatsLoading && (
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-20 w-20 bg-surface-container-lowest rounded-xl animate-pulse"
                  />
                ))}
              </div>
            )}
            {!formatsLoading && formatList.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {formatList.map((f) => {
                  const isSelected = activeFormatPathId === f.id;
                  const icon = FORMAT_ICONS[f.id] ?? 'view_quilt';
                  const isCustom = f.id === CUSTOM_FORMAT_PATH_ID;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        setSelectedFormatPathId(f.id);
                        setSelectedTemplateId(null);
                      }}
                      className={`flex-shrink-0 flex flex-col items-center justify-center gap-2 px-3 py-3 rounded-xl min-w-[72px] transition-transform active:scale-95 ${
                        isSelected
                          ? 'bg-primary text-on-primary shadow-md shadow-primary/20'
                          : 'bg-surface-container-lowest text-on-surface-variant hover:bg-primary-container transition-all'
                      }`}
                    >
                      <span className="material-symbols-outlined text-xl">{icon}</span>
                      <span className="text-[9px] font-bold uppercase tracking-tight text-center leading-tight">
                        {f.name}
                      </span>
                      {isCustom && (
                        <span className="text-[8px] font-black uppercase tracking-tighter opacity-80">
                          Custom
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 mt-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold font-headline text-on-surface">
                Templates: {selectedFormatMeta?.name ?? '—'}
              </h2>
              {!templatesLoading && (
                <span className="text-xs text-on-surface-variant">
                  {allTemplates.length} Variations
                </span>
              )}
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
                {error}
              </div>
            )}

            {templatesLoading && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-20 bg-surface-container-lowest rounded-xl border border-outline-variant/10 animate-pulse"
                  />
                ))}
              </div>
            )}

            {!templatesLoading && allTemplates.length === 0 && (
              <div className="py-8 text-center bg-surface-container-lowest rounded-xl border border-outline-variant/10">
                <span className="material-symbols-outlined text-4xl text-primary/30">
                  grid_view
                </span>
                <p className="mt-2 text-sm text-on-surface-variant">No templates in this format.</p>
              </div>
            )}

            {!templatesLoading && allTemplates.length > 0 && (
              <div className="space-y-3">
                {allTemplates.map((t: TemplateSummary) => {
                  const isSelected = selectedTemplateId === t.id;
                  const isCustomTpl = t.format_id.startsWith('custom-');
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTemplateId(t.id)}
                      className={`p-4 rounded-xl flex items-start gap-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-primary-container border-l-4 border-primary'
                          : 'bg-surface-container-lowest hover:bg-primary-container border-l-4 border-transparent hover:border-primary-container/30'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary' : 'bg-surface-container'}`}
                      >
                        <span
                          className={`material-symbols-outlined text-xl ${isSelected ? 'text-on-primary' : 'text-on-surface-variant'}`}
                        >
                          tv
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3
                            className={`font-bold text-sm truncate ${isSelected ? 'text-on-primary-container' : 'text-on-surface'}`}
                          >
                            {t.name}
                          </h3>
                          {isCustomTpl && (
                            <span className="text-[9px] uppercase font-black tracking-widest text-primary shrink-0">
                              Custom
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs mt-1 line-clamp-2 ${isSelected ? 'text-on-primary-container/80' : 'text-on-surface-variant'}`}
                        >
                          {t.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="col-span-1 lg:col-span-7 flex flex-col gap-4">
          <div className="bg-surface-container-lowest rounded-2xl p-6 shadow-sm flex flex-col relative overflow-hidden h-full min-h-[500px]">
            {selectedTemplate ? (
              <>
                <div className="flex justify-between items-center mb-6 shrink-0">
                  <div className="flex flex-col">
                    <h2 className="text-xl font-extrabold font-headline text-on-surface">
                      {selectedTemplate.name}
                    </h2>
                    <span className="text-xs text-on-surface-variant font-medium mt-1">
                      {selectedTemplate.description}
                    </span>
                  </div>
                </div>

                <div className="relative overflow-hidden flex items-center justify-center bg-surface rounded-xl p-4 flex-grow min-h-[300px]">
                  <div className="relative w-full max-w-2xl mx-auto flex flex-col items-center">
                    <div
                      className="relative w-full bg-[#0b0f14] rounded-[1rem] p-[1.5%] shadow-2xl border border-black/60 overflow-hidden"
                      style={{ aspectRatio: previewAspect }}
                    >
                      {templateDetail.loading && (
                        <div className="w-full h-full animate-pulse rounded-sm bg-surface-container" />
                      )}
                      {!templateDetail.loading && templateDetail.error && (
                        <div className="w-full h-full rounded-sm bg-surface-container flex items-center justify-center text-xs text-on-surface-variant">
                          Failed to load preview
                        </div>
                      )}
                      {!templateDetail.loading && !templateDetail.error && templateDetail.data && (
                        <TemplatePreview template={templateDetail.data} />
                      )}
                      {!templateDetail.loading && !templateDetail.data && !templateDetail.error && (
                        <div className="w-full h-full bg-gradient-to-br from-primary-container to-surface-container rounded-sm overflow-hidden flex items-center justify-center">
                          <div className="text-center space-y-3 opacity-40">
                            <span className="material-symbols-outlined text-7xl text-primary">
                              tv
                            </span>
                            <p className="text-sm font-bold text-on-surface-variant">
                              {selectedTemplate.name}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-24 md:w-32 h-2 bg-[#0f141a] rounded-b-lg" />
                      <div className="w-36 md:w-48 h-1.5 bg-[#11161d] rounded-full -mt-0.5 shadow-lg" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 shrink-0">
                  <div className="space-y-3">
                    <p className="text-[11px] font-bold text-on-surface-variant uppercase tracking-widest">
                      Mandatory Elements
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1.5 bg-primary-container text-on-surface text-[10px] font-bold rounded-lg flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px]">token</span> Logo
                      </span>
                      <span className="px-3 py-1.5 bg-primary-container text-on-surface text-[10px] font-bold rounded-lg flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px]">videocam</span>{' '}
                        Vertical Video
                      </span>
                      <span className="px-3 py-1.5 bg-primary-container text-on-surface text-[10px] font-bold rounded-lg flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px]">image</span> Product
                        Photo
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-start md:justify-end items-end">
                    <button
                      type="button"
                      onClick={handleUseTemplate}
                      className="bg-primary text-on-primary px-6 md:px-8 py-3 md:py-4 rounded-xl font-bold flex items-center justify-center gap-3 shadow-lg shadow-primary/30 hover:scale-[1.02] active:scale-[0.98] transition-all w-full md:w-auto"
                    >
                      <span className="material-symbols-outlined">edit_square</span> Use This
                      Template
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-grow flex flex-col items-center justify-center text-center p-8 opacity-60">
                <span className="material-symbols-outlined text-6xl text-on-surface-variant mb-4">
                  view_in_ar
                </span>
                <h3 className="text-lg font-bold text-on-surface font-headline mb-2">
                  Select a Template
                </h3>
                <p className="text-sm text-on-surface-variant max-w-sm">
                  Choose a template from the list on the left to preview its format and mandatory
                  elements.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        initialTemplateId={selectedTemplateId}
        initialFormatId={activeFormatPathId}
        onCreated={(created) => navigate(`/creatives/${created.id}`)}
      />
    </div>
  );
}
